import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Input } from '@/components/ui/input';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  ArrowLeft, ArrowRight, CheckCircle2, XCircle, Clock, Target,
  BookOpen, Calculator, Send, RotateCcw, Home, Award, Zap,
  ChevronLeft, ChevronRight, AlertCircle
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WorksheetView = () => {
  const navigate = useNavigate();
  const { worksheetId } = useParams();
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [worksheet, setWorksheet] = useState(null);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [results, setResults] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  useEffect(() => {
    fetchWorksheet();
  }, [worksheetId]);

  // Timer
  useEffect(() => {
    if (!showResults && worksheet) {
      const timer = setInterval(() => {
        setTimeElapsed(prev => prev + 1);
      }, 1000);
      return () => clearInterval(timer);
    }
  }, [showResults, worksheet]);

  const fetchWorksheet = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API}/adaptive/worksheet/${worksheetId}`, { headers });
      setWorksheet(res.data);
    } catch (error) {
      console.error('Error fetching worksheet:', error);
      toast.error('Failed to load worksheet');
      navigate('/adaptive');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerChange = (questionId, value) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: value
    }));
  };

  const submitWorksheet = async () => {
    try {
      setSubmitting(true);
      const res = await axios.post(
        `${API}/adaptive/worksheet/${worksheetId}/submit`,
        { worksheet_id: worksheetId, answers },
        { headers }
      );
      setResults(res.data);
      setShowResults(true);
      
      if (res.data.score >= 80) {
        toast.success('Excellent work! 🎉');
      } else if (res.data.score >= 60) {
        toast.success('Good effort! Keep practicing.');
      } else {
        toast.info('Review your answers and try again.');
      }
    } catch (error) {
      console.error('Error submitting worksheet:', error);
      toast.error('Failed to submit worksheet');
    } finally {
      setSubmitting(false);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const goToQuestion = (index) => {
    if (index >= 0 && index < worksheet.questions.length) {
      setCurrentQuestion(index);
    }
  };

  const isAnswered = (questionId) => {
    return answers[questionId] !== undefined && answers[questionId] !== '';
  };

  const getQuestionResult = (questionId) => {
    if (!results) return null;
    return results.results.find(r => r.question_id === questionId);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-purple-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-slate-500">Loading worksheet...</p>
        </div>
      </div>
    );
  }

  if (!worksheet) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white flex items-center justify-center">
        <Card className="bg-white border-slate-200/50 p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-600 mx-auto mb-4" />
          <p className="text-slate-900 mb-4">Worksheet not found</p>
          <Button onClick={() => navigate('/adaptive')}>Go Back</Button>
        </Card>
      </div>
    );
  }

  const question = worksheet.questions[currentQuestion];
  const progress = ((currentQuestion + 1) / worksheet.questions.length) * 100;
  const answeredCount = Object.keys(answers).filter(k => answers[k] !== '').length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50/30 to-white" data-testid="worksheet-view">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200/50">
        <div className="max-w-4xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                variant="ghost"
                onClick={() => navigate('/adaptive')}
                className="text-slate-500 hover:text-slate-900"
                data-testid="back-btn"
              >
                <ArrowLeft className="h-5 w-5" />
              </Button>
              <div>
                <h1 className="text-lg font-bold text-slate-900">{worksheet.title}</h1>
                <p className="text-sm text-slate-500">
                  {worksheet.subject === 'maths' ? 'Maths' : 'English'} • Week {worksheet.week}
                </p>
              </div>
            </div>
            
            <div className="flex items-center gap-4">
              <Badge variant="outline" className="bg-slate-100 text-slate-600">
                <Clock className="w-3 h-3 mr-1" />
                {formatTime(timeElapsed)}
              </Badge>
              <Badge className="bg-purple-500/20 text-purple-600">
                Band {worksheet.band}
              </Badge>
            </div>
          </div>
          
          {/* Progress Bar */}
          <div className="mt-4">
            <div className="flex justify-between text-sm mb-2">
              <span className="text-slate-500">Question {currentQuestion + 1} of {worksheet.questions.length}</span>
              <span className="text-slate-500">{answeredCount} answered</span>
            </div>
            <Progress value={progress} className="h-2" />
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        {/* Passage Display (for comprehension questions) */}
        {worksheet.passage && currentQuestion === 0 && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-6"
          >
            <Card className="bg-slate-100 border-slate-200/50">
              <CardHeader>
                <CardTitle className="text-lg text-slate-900 flex items-center gap-2">
                  <BookOpen className="w-5 h-5 text-green-600" />
                  Reading Passage
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-slate-600 leading-relaxed whitespace-pre-line">
                  {worksheet.passage}
                </p>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Question Card */}
        <AnimatePresence mode="wait">
          <motion.div
            key={currentQuestion}
            initial={{ opacity: 0, x: 50 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -50 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="bg-white border-slate-200/50 mb-6">
              <CardContent className="p-6">
                {/* Question Header */}
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-600 to-indigo-600 flex items-center justify-center text-white font-bold">
                      {currentQuestion + 1}
                    </div>
                    <div>
                      <Badge variant="outline" className="bg-slate-100 text-slate-600 mb-1">
                        {question.marks} mark{question.marks > 1 ? 's' : ''}
                      </Badge>
                    </div>
                  </div>
                  
                  {showResults && (
                    <div>
                      {getQuestionResult(question.question_id)?.is_correct ? (
                        <Badge className="bg-green-500/20 text-green-600">
                          <CheckCircle2 className="w-4 h-4 mr-1" /> Correct
                        </Badge>
                      ) : (
                        <Badge className="bg-red-500/20 text-red-600">
                          <XCircle className="w-4 h-4 mr-1" /> Incorrect
                        </Badge>
                      )}
                    </div>
                  )}
                </div>

                {/* Question Text */}
                <h2 className="text-xl text-slate-900 font-medium mb-6">
                  {question.question}
                </h2>

                {/* Answer Input */}
                <div className="space-y-4">
                  {question.type === 'multiple_choice' && question.options && (
                    <RadioGroup
                      value={answers[question.question_id] || ''}
                      onValueChange={(value) => handleAnswerChange(question.question_id, value)}
                      disabled={showResults}
                    >
                      {question.options.map((option, idx) => (
                        <div 
                          key={idx}
                          className={`flex items-center space-x-3 p-4 rounded-xl border transition-all ${
                            answers[question.question_id] === option
                              ? 'bg-purple-500/20 border-purple-500/50'
                              : 'bg-slate-100 border-slate-200/50 hover:bg-slate-100'
                          } ${
                            showResults && getQuestionResult(question.question_id)?.correct_answer === option
                              ? 'bg-green-500/20 border-green-500/50'
                              : ''
                          }`}
                        >
                          <RadioGroupItem value={option} id={`option-${idx}`} />
                          <Label 
                            htmlFor={`option-${idx}`} 
                            className="text-slate-900 cursor-pointer flex-1"
                          >
                            {option}
                          </Label>
                        </div>
                      ))}
                    </RadioGroup>
                  )}

                  {(question.type === 'fill_blank' || question.type === 'ordering') && (
                    <Input
                      type="text"
                      placeholder="Type your answer here..."
                      value={answers[question.question_id] || ''}
                      onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                      disabled={showResults}
                      className="bg-slate-100 border-slate-200 text-slate-900 text-lg p-4 h-14"
                      data-testid="answer-input"
                    />
                  )}

                  {question.type === 'short_answer' && (
                    <Textarea
                      placeholder="Write your answer here..."
                      value={answers[question.question_id] || ''}
                      onChange={(e) => handleAnswerChange(question.question_id, e.target.value)}
                      disabled={showResults}
                      className="bg-slate-100 border-slate-200 text-slate-900 min-h-[120px]"
                      data-testid="answer-textarea"
                    />
                  )}
                </div>

                {/* Show Correct Answer After Submission */}
                {showResults && (
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mt-6 p-4 rounded-xl bg-slate-100 border border-slate-200/50"
                  >
                    <p className="text-sm text-slate-500 mb-1">Correct Answer:</p>
                    <p className="text-green-600 font-medium">
                      {getQuestionResult(question.question_id)?.correct_answer}
                    </p>
                  </motion.div>
                )}
              </CardContent>
            </Card>
          </motion.div>
        </AnimatePresence>

        {/* Question Navigation Dots */}
        <div className="flex flex-wrap justify-center gap-2 mb-6">
          {worksheet.questions.map((q, idx) => (
            <button
              key={idx}
              onClick={() => goToQuestion(idx)}
              className={`w-10 h-10 rounded-lg flex items-center justify-center text-sm font-medium transition-all ${
                idx === currentQuestion
                  ? 'bg-purple-600 text-white'
                  : isAnswered(q.question_id)
                    ? showResults
                      ? getQuestionResult(q.question_id)?.is_correct
                        ? 'bg-green-500/30 text-green-600 border border-green-500/50'
                        : 'bg-red-500/30 text-red-600 border border-red-500/50'
                      : 'bg-slate-100 text-slate-900'
                    : 'bg-slate-100 text-slate-500 border border-slate-200/50'
              }`}
              data-testid={`question-nav-${idx}`}
            >
              {idx + 1}
            </button>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => goToQuestion(currentQuestion - 1)}
            disabled={currentQuestion === 0}
            className="bg-slate-100 border-slate-200"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Previous
          </Button>

          {!showResults ? (
            currentQuestion === worksheet.questions.length - 1 ? (
              <Button
                onClick={submitWorksheet}
                disabled={submitting || answeredCount === 0}
                className="bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700"
                data-testid="submit-worksheet-btn"
              >
                {submitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4 mr-2" />
                    Submit Worksheet
                  </>
                )}
              </Button>
            ) : (
              <Button
                onClick={() => goToQuestion(currentQuestion + 1)}
                className="bg-gradient-to-r from-purple-600 to-indigo-600"
              >
                Next
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )
          ) : (
            <Button
              onClick={() => navigate('/adaptive')}
              className="bg-gradient-to-r from-purple-600 to-indigo-600"
            >
              <Home className="w-4 h-4 mr-2" />
              Back to Learning Path
            </Button>
          )}
        </div>

        {/* Results Summary */}
        {showResults && results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mt-8"
          >
            <Card className="bg-white border-slate-200/50">
              <CardContent className="p-6">
                <div className="text-center mb-6">
                  <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                    results.score >= 80 
                      ? 'bg-green-500/20' 
                      : results.score >= 60 
                        ? 'bg-yellow-500/20' 
                        : 'bg-red-500/20'
                  }`}>
                    {results.score >= 80 ? (
                      <Award className="w-10 h-10 text-green-600" />
                    ) : results.score >= 60 ? (
                      <Target className="w-10 h-10 text-yellow-400" />
                    ) : (
                      <RotateCcw className="w-10 h-10 text-red-600" />
                    )}
                  </div>
                  <h3 className="text-3xl font-bold text-slate-900 mb-2">{results.score}%</h3>
                  <p className="text-slate-500">
                    {results.earned_marks} / {results.total_marks} marks
                  </p>
                  <p className="text-lg text-slate-600 mt-2">{results.message}</p>
                </div>

                <div className="flex justify-center gap-4">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowResults(false);
                      setResults(null);
                      setAnswers({});
                      setCurrentQuestion(0);
                      setTimeElapsed(0);
                    }}
                    className="bg-slate-100 border-slate-200"
                  >
                    <RotateCcw className="w-4 h-4 mr-2" />
                    Try Again
                  </Button>
                  <Button
                    onClick={() => navigate('/adaptive')}
                    className="bg-gradient-to-r from-purple-600 to-indigo-600"
                  >
                    Continue Learning
                    <ChevronRight className="w-4 h-4 ml-1" />
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Wrong Answers Review Section */}
            {results.results && results.results.filter(r => !r.is_correct).length > 0 && (
              <Card className="bg-white border-red-500/30 border-2 mt-6" data-testid="wrong-answers-review">
                <CardHeader className="bg-red-900/20">
                  <CardTitle className="text-slate-900 flex items-center gap-2">
                    <XCircle className="h-5 w-5 text-red-600" />
                    Review Wrong Answers
                    <Badge variant="outline" className="ml-2 border-red-500/50 text-red-600">
                      {results.results.filter(r => !r.is_correct).length} questions
                    </Badge>
                  </CardTitle>
                  <p className="text-slate-500 text-sm">Learn from your mistakes to improve</p>
                </CardHeader>
                <CardContent className="space-y-4 pt-4">
                  {results.results.filter(r => !r.is_correct).map((result, idx) => (
                    <div 
                      key={result.question_id}
                      className="p-4 bg-slate-100 rounded-lg border border-red-500/20"
                      data-testid={`wrong-answer-${idx}`}
                    >
                      {/* Question Number & Topic */}
                      <div className="flex items-center gap-2 mb-3">
                        <Badge variant="outline" className="border-red-500/50 text-red-600">
                          Q{idx + 1}
                        </Badge>
                        {result.topic && (
                          <Badge variant="outline" className="border-slate-300 text-slate-500 capitalize">
                            {result.topic.replace(/_/g, ' ')}
                          </Badge>
                        )}
                      </div>

                      {/* Question Text */}
                      <p className="text-slate-900 font-medium mb-4">
                        {result.question_text || 'Question not available'}
                      </p>

                      {/* Options (for multiple choice) */}
                      {result.options && result.options.length > 0 && (
                        <div className="grid gap-2 mb-4">
                          {result.options.map((option, optIdx) => {
                            const isUserAnswer = optIdx === result.your_answer;
                            const isCorrectAnswer = optIdx === result.correct_answer;
                            
                            return (
                              <div
                                key={optIdx}
                                className={`p-3 rounded-lg border flex items-center gap-3 ${
                                  isCorrectAnswer 
                                    ? 'bg-green-900/30 border-green-500/50' 
                                    : isUserAnswer 
                                      ? 'bg-red-900/30 border-red-500/50'
                                      : 'bg-slate-100 border-slate-200/50'
                                }`}
                              >
                                <span className={`font-bold ${
                                  isCorrectAnswer ? 'text-green-600' : 
                                  isUserAnswer ? 'text-red-600' : 'text-slate-500'
                                }`}>
                                  {String.fromCharCode(65 + optIdx)}.
                                </span>
                                <span className={
                                  isCorrectAnswer ? 'text-green-600' : 
                                  isUserAnswer ? 'text-red-600' : 'text-slate-600'
                                }>
                                  {option}
                                </span>
                                {isCorrectAnswer && (
                                  <CheckCircle2 className="h-4 w-4 text-green-600 ml-auto" />
                                )}
                                {isUserAnswer && !isCorrectAnswer && (
                                  <XCircle className="h-4 w-4 text-red-600 ml-auto" />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}

                      {/* Text Answer (for non-MCQ) */}
                      {(!result.options || result.options.length === 0) && (
                        <div className="flex flex-wrap gap-4 mb-4 text-sm">
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">Your answer:</span>
                            <span className="text-red-600 font-medium">
                              {result.your_answer !== null && result.your_answer !== undefined
                                ? String(result.your_answer)
                                : 'Not answered'}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-slate-500">Correct answer:</span>
                            <span className="text-green-600 font-medium">
                              {String(result.correct_answer)}
                            </span>
                          </div>
                        </div>
                      )}

                      {/* Explanation */}
                      {result.explanation && (
                        <div className="p-3 bg-blue-900/20 rounded-lg border border-blue-500/30">
                          <div className="flex items-center gap-2 mb-2">
                            <BookOpen className="h-4 w-4 text-blue-600" />
                            <span className="text-blue-600 font-medium text-sm">Explanation</span>
                          </div>
                          <p className="text-slate-600 text-sm">{result.explanation}</p>
                        </div>
                      )}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}
          </motion.div>
        )}
      </main>
    </div>
  );
};

export default WorksheetView;
